import { useCallback, useRef, useState } from 'react'
import { describeError, GRABBITBOT_ERROR_MESSAGES } from '../../api/errorMessages'
import {
  ApiClientError,
  TRANSACTION_CARD_DECLINED_ERROR_MESSAGES,
  TRANSACTION_ERROR_MESSAGES,
  checkoutRedirectUrl,
  redirectToCheckout,
  type CheckoutSession,
  type PaymentMethod,
  type Plan,
  type Transaction,
  useApiQuery,
} from 'grabbitbot-legacy-ui'
import { paymentClient, portalClient } from '../../api'
import type { ChargeRequest, PlanQuote } from '../../api/types'
import { useKeyedActions } from '../../hooks/useKeyedActions'

/**
 * Money-flow plumbing for the B5 payment pages (BUSINESS-CRITICAL — card
 * charges and Stripe Checkout; contract
 * `legacy-migration/docs/analysis/2026-09-27-grabbitbot-b5-contract.md`,
 * ticket 4fc3bfc4). Two ways to pay, both ported from the legacy
 * `PaymentComponent` / `PlanListComponent` / `ProductListComponent` /
 * `PaymentCreditViewComponent`:
 *
 *  1. **Charge the saved card** (`POST /payment/startonline`, legacy
 *     `payPlan`/`payProduct`/`payCredit`). Every submit carries an
 *     `Idempotency-Key`: a FRESH one per user-initiated submit, REUSED only
 *     when the user retries the SAME purchase after an unknown outcome
 *     (5xx / network) — the backend then resumes or replays that charge
 *     instead of charging again. Never retried automatically.
 *  2. **Stripe Checkout** (`POST /payment/sessions`): redirect to the
 *     backend-returned URL, and ONLY if it is an absolute https URL
 *     (`checkoutRedirectUrl`, grabbitbot-legacy-ui 0.4.2). The session id
 *     and the page to come back to are remembered in `sessionStorage` (the
 *     legacy keys), the return target as a same-site PATH only.
 *
 * A declined charge is a 200 with a `Transaction` of `status: 9` — it is
 * shown as a failure with the legacy card-decline wording, never as success.
 */

export interface PaymentFeedback {
  tone: 'success' | 'info' | 'warning' | 'error'
  text: string
}

// --- legacy status vocabularies (transaction list / subscription cards) ---

/** `Transaction.status` (legacy transaction list template). */
export const TRANSACTION_STATUS_LABELS: Record<number, string> = {
  0: 'Pending',
  1: 'Finished',
  6: 'Canceled',
  9: 'Failed',
}
export const TRANSACTION_TYPE_LABELS: Record<string, string> = {
  monthlyfee: 'Monthly fee',
  weeklyfee: 'Weekly commission fee',
  adhoc: 'Ad-hoc',
  onetime: 'Onetime payment',
  plan: 'Subscription',
  product: 'Product payment',
  credit: 'Credit buy',
  custom: 'Custom payment',
}
export const TRANSACTION_CAUSE_LABELS: Record<string, string> = {
  setup: 'Payment setup',
  pmchange: 'Payment method change',
  manual: 'Manual payment',
  renew: 'Payment renew',
  startup: 'Project startup',
  serverinstall: 'Server install',
}
export const TRANSACTION_MODE_LABELS: Record<string, string> = {
  online: 'Started by user',
  offline: 'Automatic',
}
export const TRANSACTION_RESULT_LABELS: Record<number, string> = {
  0: 'Unknown',
  1: 'Success',
  2: 'Card expired',
  3: 'Transaction declined (authentication needed)',
  6: 'Canceled',
  9: 'Transaction declined',
}
/** `Subscription.status` (legacy subscription card headers). */
export const SUBSCRIPTION_STATUS_LABELS: Record<number, string> = {
  0: 'Not started',
  1: 'Active',
  3: 'Unpaid',
  6: 'Canceled',
  9: 'Inactive',
}
export const TRANSACTION_STATUS_FAILED = 9

/** Legacy `processBackgroundTransaction` wording for a failed transaction.
 * Deliberate fix (documented, changelog 0.7.0): legacy looked the
 * card-decline text up by `stripeCode` instead of `declineCode`, which
 * never matched and showed "undefined" — this reads `declineCode`. */
export function declineMessage(transaction: Pick<Transaction, 'details'>): string {
  let message = TRANSACTION_ERROR_MESSAGES.general ?? "We couldn't process this transaction."
  const details = transaction.details
  if (details?.stripeCode && Object.prototype.hasOwnProperty.call(TRANSACTION_ERROR_MESSAGES, details.stripeCode)) {
    message = TRANSACTION_ERROR_MESSAGES[details.stripeCode] ?? message
    if (
      details.stripeCode === 'card_declined' &&
      details.declineCode &&
      Object.prototype.hasOwnProperty.call(TRANSACTION_CARD_DECLINED_ERROR_MESSAGES, details.declineCode)
    ) {
      message = TRANSACTION_CARD_DECLINED_ERROR_MESSAGES[details.declineCode] ?? message
    }
  }
  return message
}

/** What a `POST /payment/startonline` 200 answer means for the user, and
 * whether the idempotency key must be kept for a retry of the same submit. */
export function describeChargeResult(transaction: Transaction | null | undefined): {
  feedback: PaymentFeedback
  keepKey: boolean
} {
  if (!transaction || typeof transaction !== 'object') {
    // Legacy answers `null` when the outcome could not be established.
    return {
      feedback: {
        tone: 'warning',
        text: 'The payment was not confirmed. Check Transactions before trying again — retrying this same payment will not charge twice.',
      },
      keepKey: true,
    }
  }
  if (transaction.status === TRANSACTION_STATUS_FAILED) {
    return { feedback: { tone: 'error', text: `Transaction failed! ${declineMessage(transaction)}` }, keepKey: false }
  }
  if (transaction.status === 1) {
    return { feedback: { tone: 'success', text: 'Payment successful.' }, keepKey: false }
  }
  if (transaction.status === 0) {
    return {
      feedback: { tone: 'info', text: 'The payment is still pending. Check Transactions for the final result.' },
      keepKey: false,
    }
  }
  return {
    feedback: { tone: 'warning', text: 'The payment was not completed. Check Transactions for details.' },
    keepKey: false,
  }
}

type ChargeErrorClass = {
  feedback: PaymentFeedback
  /** Keep the SAME key: the next submit of this purchase is a retry of an
   * unknown outcome, which the backend resumes / replays. */
  keepKey: boolean
  /** Refuse any further charge of this purchase in this view. */
  lock: boolean
}

/** Named refusals of a plan purchase that happen BEFORE anything is
 * charged (api.grabbitbot.com 0.15.0 / feesubscription 0.15.0). */
const PURCHASE_REFUSAL_CODES: ReadonlySet<string> = new Set([
  'replaced_plan_not_running',
  'fully_covered_use_startonline',
  'invalid_replaces_plan',
])

const OUTCOME_UNKNOWN_CONTACT_SUPPORT =
  'Outcome unknown — do not retry, contact support. This payment may or may not have been charged.'

/** Classifies a failed `POST /payment/startonline`. The backend relays a
 * payment-service failure as 502 `{"error":"payment_service_error",
 * "status":<upstream>}`, so the upstream status decides what is known. */
export function classifyChargeError(err: unknown): ChargeErrorClass {
  if (err instanceof ApiClientError) {
    const upstream =
      err.status === 502 && err.code === 'payment_service_error' && err.raw && typeof err.raw === 'object'
        ? Number((err.raw as Record<string, unknown>).status)
        : null
    if ((err.status === 409 && err.code === 'charge_outcome_unknown') || upstream === 409) {
      return { feedback: { tone: 'error', text: OUTCOME_UNKNOWN_CONTACT_SUPPORT }, keepKey: false, lock: true }
    }
    if (PURCHASE_REFUSAL_CODES.has(err.code) && (err.status === 409 || err.status === 400)) {
      // Refused by name BEFORE any charge (plan switch / balance cover).
      return {
        feedback: { tone: 'error', text: describeError(err, 'The payment was refused') },
        keepKey: false,
        lock: false,
      }
    }
    if (err.status === 409 && err.code === 'request_in_progress') {
      return {
        feedback: {
          tone: 'warning',
          text: 'This payment is still being processed. Wait a minute, then check Transactions before trying again.',
        },
        keepKey: true,
        lock: false,
      }
    }
    if (err.status === 422 && err.code === 'idempotency_key_reused') {
      return {
        feedback: { tone: 'error', text: 'The payment request was refused (it did not match the earlier attempt). Nothing new was charged.' },
        keepKey: false,
        lock: false,
      }
    }
    if (err.status === 503 && err.code === 'payment_service_unavailable') {
      // The payment service could not be reached: the request may or may not have arrived,
      // so the charge may have happened. Keep the key — the same submit resumes it.
      return {
        feedback: {
          tone: 'warning',
          text: 'Outcome unknown — the payment service could not be reached, so the payment may have gone through. Check Transactions; retrying this same payment is safe and will not charge twice.',
        },
        keepKey: true,
        lock: false,
      }
    }
    if (err.code === 'payment_auth_failed') {
      // The payment service refused our access (401/403) before processing anything.
      return {
        feedback: { tone: 'error', text: describeError(err, 'The payment was refused') },
        keepKey: false,
        lock: false,
      }
    }
    if (err.status === 503) {
      return {
        feedback: { tone: 'error', text: 'Payments are not available right now. Nothing was charged.' },
        keepKey: false,
        lock: false,
      }
    }
    if (err.status === 0 || err.status >= 500) {
      // Network failure, a 5xx, or the payment service answering 5xx
      // through the backend's 502: the charge may have happened.
      if (upstream !== null && upstream >= 400 && upstream < 500) {
        return {
          feedback: { tone: 'error', text: 'The payment was refused. Nothing was charged.' },
          keepKey: false,
          lock: false,
        }
      }
      return {
        feedback: {
          tone: 'warning',
          text: 'Outcome unknown — the payment may have gone through. Check Transactions; retrying this same payment is safe and will not charge twice.',
        },
        keepKey: true,
        lock: false,
      }
    }
    return { feedback: { tone: 'error', text: describeError(err, 'The payment failed') }, keepKey: false, lock: false }
  }
  return {
    feedback: { tone: 'warning', text: 'Outcome unknown — check Transactions before trying again.' },
    keepKey: true,
    lock: false,
  }
}

export function describePaymentError(err: unknown): PaymentFeedback {
  if (err instanceof CheckoutStartError) {
    return { tone: 'error', text: 'Could not start the checkout. Nothing was charged.' }
  }
  if (err instanceof ApiClientError) {
    if (err.status === 503 && !GRABBITBOT_ERROR_MESSAGES[err.code]) {
      return { tone: 'error', text: 'Payments are not available right now.' }
    }
    if (err.status === 502 && !GRABBITBOT_ERROR_MESSAGES[err.code]) {
      return { tone: 'error', text: 'The payment service did not answer. Please try again later.' }
    }
    return { tone: 'error', text: describeError(err, 'The request failed') }
  }
  return { tone: 'error', text: describeError(err, 'The request failed') }
}

export function newIdempotencyKey(): string {
  return crypto.randomUUID()
}

// --- Checkout redirect + return --------------------------------------------

/** Legacy `PaymentService.setSessionId` / `setLastPaymentPageUrl` keys. */
export const CHECKOUT_SESSION_KEY = 'checkoutSessionId'
export const RETURN_PATH_KEY = 'lastPaymentPageUrl'

function storageGet(key: string): string {
  try {
    return sessionStorage.getItem(key) ?? ''
  } catch {
    return ''
  }
}
function storageSet(key: string, value: string) {
  try {
    sessionStorage.setItem(key, value)
  } catch {
    /* storage unavailable — the return page then falls back to /payment */
  }
}
function storageRemove(key: string) {
  try {
    sessionStorage.removeItem(key)
  } catch {
    /* ignore */
  }
}

/** A return target is only ever a path on THIS site: starts with a single
 * `/`, no scheme, no protocol-relative `//`, no backslash tricks. Anything
 * else falls back to `/payment`. (Legacy stored and followed a full
 * `location.href`.) */
export function safeReturnPath(value: string | null | undefined): string {
  if (!value || typeof value !== 'string') return '/payment'
  if (!value.startsWith('/') || value.startsWith('//') || value.includes('\\')) return '/payment'
  for (let i = 0; i < value.length; i++) if (value.charCodeAt(i) < 0x20 || value.charCodeAt(i) === 0x7f) return '/payment'
  if (value.startsWith('/payment/success') || value.startsWith('/payment/cancel')) return '/payment'
  return value
}

export function storedCheckoutSessionId(): string {
  return storageGet(CHECKOUT_SESSION_KEY)
}

export function storedReturnPath(): string {
  return safeReturnPath(storageGet(RETURN_PATH_KEY))
}

export function clearCheckoutState() {
  storageRemove(CHECKOUT_SESSION_KEY)
  storageRemove(RETURN_PATH_KEY)
}

/** The named payment-service failure classes of api.grabbitbot.com >= 0.27.0 (the old backend
 * answered these with a 200 and the upstream body instead). */
const PAYMENT_SERVICE_FAILURE_CODES: ReadonlySet<string> = new Set([
  'payment_auth_failed',
  'payment_auth_unavailable',
  'payment_service_unavailable',
  'payment_service_error',
  'payment_service_not_configured',
])

export function isPaymentServiceFailure(err: unknown): boolean {
  return err instanceof ApiClientError && PAYMENT_SERVICE_FAILURE_CODES.has(err.code)
}

export class CheckoutStartError extends Error {
  constructor() {
    super('checkout session has no usable https redirect URL')
    this.name = 'CheckoutStartError'
  }
}

/** Remembers where to come back to and redirects to the Checkout page —
 * or throws `CheckoutStartError` (nothing stored, no navigation) when the
 * answer carries no session id or no https URL (the backend relays a
 * payment-service refusal as a 200 like `{"error":"invalid_type"}`). */
export function beginCheckout(session: CheckoutSession | null | undefined, returnPath: string) {
  if (!session || typeof session !== 'object' || !session.id || !checkoutRedirectUrl(session.url)) {
    throw new CheckoutStartError()
  }
  storageSet(CHECKOUT_SESSION_KEY, String(session.id))
  storageSet(RETURN_PATH_KEY, safeReturnPath(returnPath))
  redirectToCheckout(session)
}

/** What a `PATCH /payment/sessions/{id}/check` answer means. api.grabbitbot.com >= 0.27.0 answers
 * a payment-service failure with a non-2xx named error (the caller passes `null`); the older
 * backend answered the upstream content with HTTP 200 whatever happened (a string like
 * `"upstream down"`, an `{error}` object, or the session). Both stay handled: only a `complete`
 * session whose payment is settled counts as success. */
export function describeCheckResult(result: unknown): PaymentFeedback {
  if (result && typeof result === 'object' && !Array.isArray(result)) {
    const session = result as Record<string, unknown>
    const paymentStatus = session.payment_status
    const settled = paymentStatus === undefined || paymentStatus === null || paymentStatus === 'paid' || paymentStatus === 'no_payment_required'
    if (session.status === 'complete' && settled && !session.error) {
      return { tone: 'success', text: 'Payment received. Thank you!' }
    }
    if (session.status === 'expired') {
      return { tone: 'warning', text: 'This checkout expired. Nothing was charged.' }
    }
    if (session.status === 'open') {
      return { tone: 'warning', text: 'The payment was not completed. Nothing was charged.' }
    }
  }
  return {
    tone: 'warning',
    text: 'We could not confirm this payment yet. Do not pay again — check Transactions in a few minutes, or contact support.',
  }
}

// --- the page-level hook ----------------------------------------------------

export function hasUsablePaymentMethod(method: PaymentMethod | null | undefined): boolean {
  return Boolean(method && method.setup && method.primaryPaymentMethodId)
}

/** Legacy cause selection for a Checkout session (`startProductPaymentNewMethod`
 * / `startCreditPaymentNewMethod`): paying with a NEW method is `setup`
 * (no card yet) or `pmchange` (replacing the card); a plain one-off
 * checkout without saving the method is `manual`. A card on file that is
 * not set up forces the setup path, as legacy did. */
export function checkoutCause(method: PaymentMethod | null | undefined, setupMethod: boolean): { cause: string; setupMethod: boolean } {
  const hasMethod = Boolean(method && method.setup)
  const forceSetup = setupMethod || Boolean(method && !method.setup)
  return { cause: forceSetup ? (hasMethod ? 'pmchange' : 'setup') : 'manual', setupMethod: forceSetup }
}

/**
 * Per-purchase money actions for a page: the shared keyed double-submit
 * guard (`useKeyedActions`) plus the idempotency-key lifecycle.
 *
 *  - `charge(actionKey, request, onDone)` — saved-card charge.
 *  - `checkout(actionKey, create)` — Stripe Checkout redirect.
 *  - `isRetry(actionKey)` — a key is being kept after an unknown outcome,
 *    so the next submit is a retry of the SAME charge (label the button).
 *  - `isLocked(actionKey)` — `charge_outcome_unknown`: further charges of
 *    this purchase are refused in this view.
 */
export function usePaymentActions() {
  const actions = useKeyedActions<PaymentFeedback>(describeAnyPaymentError)
  const retainedKeys = useRef(new Map<string, { fingerprint: string; key: string }>())
  const [retryKeys, setRetryKeys] = useState<ReadonlySet<string>>(new Set())
  const [lockedKeys, setLockedKeys] = useState<ReadonlySet<string>>(new Set())
  const locked = useRef(new Set<string>())
  const { run, setFeedback } = actions

  const syncRetry = useCallback(() => setRetryKeys(new Set(retainedKeys.current.keys())), [])

  const charge = useCallback(
    (actionKey: string, request: ChargeRequest, onDone?: () => void) => {
      if (locked.current.has(actionKey)) return Promise.resolve()
      const fingerprint = JSON.stringify(request)
      const retained = retainedKeys.current.get(actionKey)
      const key = retained && retained.fingerprint === fingerprint ? retained.key : newIdempotencyKey()
      return run(
        actionKey,
        async () => {
          // Kept BEFORE the call: whatever happens next, a retry of this
          // exact submit must reuse this key until an answer says otherwise.
          retainedKeys.current.set(actionKey, { fingerprint, key })
          try {
            const transaction = await portalClient.startOnline(request, key)
            const { feedback, keepKey } = describeChargeResult(transaction)
            if (!keepKey) retainedKeys.current.delete(actionKey)
            return feedback
          } catch (err) {
            const cls = classifyChargeError(err)
            if (!cls.keepKey) retainedKeys.current.delete(actionKey)
            if (cls.lock) {
              locked.current.add(actionKey)
              setLockedKeys(new Set(locked.current))
            }
            throw new ChargeFailure(cls.feedback)
          } finally {
            syncRetry()
          }
        },
        (feedback) => {
          setFeedback(actionKey, feedback)
          onDone?.()
        },
      )
    },
    [run, setFeedback, syncRetry],
  )

  const checkout = useCallback(
    (actionKey: string, create: (oldSessionId: string | undefined) => Promise<CheckoutSession>) =>
      run(actionKey, async () => {
        let session: CheckoutSession
        try {
          session = await create(storedCheckoutSessionId() || undefined)
        } catch (err) {
          // Creating a Checkout session charges nothing: a payment-service failure here
          // (new named errors, or any non-2xx) is "could not start", never an unknown outcome.
          if (isPaymentServiceFailure(err)) throw new CheckoutStartError()
          throw err
        }
        beginCheckout(session, window.location.pathname + window.location.search)
      }),
    [run],
  )

  return {
    ...actions,
    charge,
    checkout,
    isRetry: (key: string) => retryKeys.has(key),
    isLocked: (key: string) => lockedKeys.has(key),
  }
}

/** Carries an already-classified charge failure through `useKeyedActions`
 * (whose `describe` then just unwraps it). */
export class ChargeFailure extends Error {
  feedback: PaymentFeedback
  constructor(feedback: PaymentFeedback) {
    super(feedback.text)
    this.name = 'ChargeFailure'
    this.feedback = feedback
  }
}

/** The `describe` of `usePaymentActions`: an already-classified charge
 * failure keeps its own wording, anything else goes through
 * `describePaymentError`. */
export function describeAnyPaymentError(err: unknown): PaymentFeedback {
  if (err instanceof ChargeFailure) return err.feedback
  return describePaymentError(err)
}

// --- number formatting ------------------------------------------------------

/** Legacy `formatNumberN`: thousands separator `,`, at most `n` decimals,
 * trailing zeros dropped (`12.50` -> `12.5`, `1000` -> `1,000`). Display
 * only — never used for arithmetic. */
export function formatNumberN(value: number | null | undefined, n = 2): string {
  const num = Number(value ?? 0)
  if (!Number.isFinite(num)) return '0'
  const fixed = Math.abs(num).toFixed(n)
  let [intPart = '0', dec = ''] = fixed.split('.')
  intPart = intPart.replace(/\B(?=(\d{3})+(?!\d))/g, ',')
  dec = dec.replace(/0+$/, '')
  const sign = num < 0 && (Number(intPart.replace(/,/g, '')) > 0 || dec !== '') ? '-' : ''
  return dec ? `${sign}${intPart}.${dec}` : `${sign}${intPart}`
}

export function priceText(currency: string | null | undefined, price: number | null | undefined): string {
  return `${(currency ?? '').toUpperCase()} ${formatNumberN(price ?? 0, 2)}`.trim()
}

/** Credit purchase bounds (legacy `minCreditCount` / `maxCreditCount`). */
export const MIN_CREDIT_COUNT = 3
export const MAX_CREDIT_COUNT = 10000

/** Validates the typed credit amount WITHOUT turning it into a float for the
 * request: digits only, within the legacy bounds. Returns the string to
 * send, or an error message. */
export function validateCreditCount(typed: string): { ok: true; value: string } | { ok: false; message: string } {
  const value = typed.trim()
  if (!/^\d+$/.test(value) || Number(value) < MIN_CREDIT_COUNT || Number(value) > MAX_CREDIT_COUNT) {
    return { ok: false, message: `Please enter a whole number between ${MIN_CREDIT_COUNT} and ${MAX_CREDIT_COUNT}!` }
  }
  return { ok: true, value }
}

// --- shared page data --------------------------------------------------------

/** The reads every legacy payment page made first (`viewBillingAccount`,
 * then the payment method + billing). `refetch` re-runs all of them — after
 * a charge / cancel / restart. A failed read degrades to `null`, never to a
 * made-up value. */
export function usePaymentBasics() {
  const [generation, setGeneration] = useState(0)
  const account = useApiQuery(() => paymentClient.viewBillingAccount(), [generation])
  const method = useApiQuery(() => paymentClient.viewPaymentMethod(), [generation])
  const billing = useApiQuery(() => paymentClient.viewBilling(), [generation])
  const refetch = useCallback(() => {
    paymentClient.clearCache()
    setGeneration((g) => g + 1)
  }, [])
  return {
    generation,
    refetch,
    account,
    method: method.data ?? null,
    methodLoading: method.loading,
    methodError: method.error,
    billing: billing.data ?? null,
  }
}

// --- balance-first purchase + plan switch (owner decisions 2026-10-13) -------

/** The three bot plans (legacy `PaymentComponent.refreshData`, and the
 * backend's bot-status `BOT_PLANS`): only these can replace one another. */
export const BOT_PLAN_IDS: ReadonlySet<string> = new Set(['5124773617074176', '5734928683630592', '6259205610668032'])

const DECIMAL_RE = /^(-)?(\d+)(?:\.(\d+))?$/

/** An exact decimal string as an integer + scale (no float anywhere on the
 * money path), or `null` when it is not a plain decimal. */
export function parseDecimal(value: unknown): { units: bigint; scale: number } | null {
  if (typeof value !== 'string') return null
  const match = DECIMAL_RE.exec(value.trim())
  if (!match) return null
  const fraction = match[3] ?? ''
  const units = BigInt(`${match[2]}${fraction}`)
  return { units: match[1] ? -units : units, scale: fraction.length }
}

/** -1 / 0 / 1 for two decimal strings, or `null` when either is not one. */
export function compareDecimal(a: unknown, b: unknown): number | null {
  const x = parseDecimal(a)
  const y = parseDecimal(b)
  if (!x || !y) return null
  const scale = Math.max(x.scale, y.scale)
  const ten = BigInt(10)
  const xu = x.units * ten ** BigInt(scale - x.scale)
  const yu = y.units * ten ** BigInt(scale - y.scale)
  return xu === yu ? 0 : xu < yu ? -1 : 1
}

/** A quote is shown only when every money field is a decimal string — a
 * broken answer is an error, never a price. */
export function isUsableQuote(quote: unknown): quote is PlanQuote {
  if (!quote || typeof quote !== 'object') return false
  const q = quote as Record<string, unknown>
  const money = ['fee', 'card', 'creditBefore', 'creditAfter'].every((k) => parseDecimal(q[k]) !== null)
  const proration = q.prorationCredit === null || q.prorationCredit === undefined || parseDecimal(q.prorationCredit) !== null
  const estimate = q.nextFeeEstimate === null || q.nextFeeEstimate === undefined || parseDecimal(q.nextFeeEstimate) !== null
  const cover =
    Array.isArray(q.balanceCover) &&
    q.balanceCover.every((c) => c && typeof c === 'object' && typeof c.currency === 'string' && parseDecimal(c.amount) !== null)
  return money && proration && estimate && cover
}

export type FeeWarning =
  /** the credit left after paying is below the next usage-fee estimate */
  | { kind: 'below_next_fee'; creditAfter: string; estimate: string; currency: string }
  /** the estimate is unknown: said so, never hidden */
  | { kind: 'fee_unknown'; reason: string }
  | null

/** What the preview means: pay from the balances (no card at all) or the
 * card remainder, and whether to warn about the next monthly usage fee. */
export function purchaseDecision(quote: PlanQuote): { payFromBalance: boolean; warning: FeeWarning } {
  const payFromBalance = compareDecimal(quote.card, '0') !== 1
  let warning: FeeWarning = null
  if (quote.nextFeeEstimate === null || quote.nextFeeEstimate === undefined) {
    warning = { kind: 'fee_unknown', reason: quote.nextFeeReason ?? 'unknown' }
  } else if (compareDecimal(quote.creditAfter, quote.nextFeeEstimate) === -1) {
    warning = {
      kind: 'below_next_fee',
      creditAfter: quote.creditAfter,
      estimate: quote.nextFeeEstimate,
      currency: quote.nextFeeCurrency || 'credit',
    }
  }
  return { payFromBalance, warning }
}

/** Two previews of the same purchase charge the same way: the fee, the
 * card remainder, every balance line and the proration all equal. A pay
 * button re-reads the quote first and refuses to charge when this fails. */
export function sameCharge(a: PlanQuote, b: PlanQuote): boolean {
  const eq = (x: unknown, y: unknown) => (x === null || x === undefined ? y === null || y === undefined : compareDecimal(x, y) === 0)
  if (!eq(a.fee, b.fee) || !eq(a.card, b.card) || !eq(a.prorationCredit, b.prorationCredit)) return false
  if (a.balanceCover.length !== b.balanceCover.length) return false
  return a.balanceCover.every((c, i) => c.currency === b.balanceCover[i]?.currency && eq(c.amount, b.balanceCover[i]?.amount))
}

/** The OTHER bot plans the user could replace with `target`: active (1) or
 * cancelled-but-paid (6) with a future expiry — exactly what the payment
 * service accepts as `replacesPlan`. Empty when `target` is not a bot plan. */
export function replaceablePlans(plans: Plan[] | null | undefined, target: Plan, now = Date.now()): Plan[] {
  if (!plans || !target.id || !BOT_PLAN_IDS.has(target.id)) return []
  return plans.filter((p) => {
    if (!p.id || p.id === target.id || !BOT_PLAN_IDS.has(p.id)) return false
    const sub = p.subscription
    if (!sub || (sub.status !== 1 && sub.status !== 6) || !sub.expiresAt) return false
    const expires = new Date(sub.expiresAt).getTime()
    return Number.isFinite(expires) && expires > now
  })
}

/** "12.50 USDT" — the decimal string as sent, the currency upper-cased
 * (`credit` stays a word). Display only. */
export function moneyText(amount: string | null | undefined, currency: string | null | undefined): string {
  const cur = (currency ?? '').toLowerCase() === 'credit' ? 'credit' : (currency ?? '').toUpperCase()
  return `${amount ?? '—'} ${cur}`.trim()
}

/** True when the quote call failed because the payment service has no
 * quote yet (older than 0.15.0): keep today's purchase flow. */
export function isQuoteUnavailable(err: unknown): boolean {
  return err instanceof ApiClientError && err.code === 'quote_unavailable'
}
